import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'primereact/chart';
import { LabelBubble } from './LabelBubble';
import { ExpensesChartData } from '../../types/expensesChartData';
import { CategoriesItem } from '../../types/categoriesItem';
import { convertValueToUSD } from '../../../../helpers/currency.helper';

// Utility function to calculate percentages
const calculatePercentages = (data: CategoriesItem[]) => {
  if (!data.length) return [];

  const total = data.reduce((sum, item) => sum + item.total, 0);

  return data.map((item) => ({
    ...item,
    percentage: ((item.total / total) * 100).toFixed(2),
  }));
};

// Utility function to extract data from the data array
const extractData = (data: CategoriesItem[]): number[] => data.map((item) => item.total);

interface DoughnutChartProps {
  expensesChartData: ExpensesChartData;
}

export const DoughnutChart = ({ expensesChartData }: DoughnutChartProps) => {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [activeIndex, setActiveIndex] = useState(-1);
  const chartRef = useRef<any>();
  const [activeCategory, setActiveCategory] = useState<CategoriesItem | null>(null);

  const getColorSet = () => [
    // Replace first color to `bw-04` if there are no categories (empty state)
    expensesChartData?.categoriesItems.length > 0 ? '#0245A2' : '#C4CBDC', // action05 | bw-04
    '#0E5CF4', // blue03
    '#94D9EF', // action02New
    '#DBF0F7', // action01New
    '#C5D1FF',
  ];

  const getColoredCategories = () => {
    if (!expensesChartData?.categoriesItems) return [];
    return expensesChartData.categoriesItems.map((item, index) => ({
      ...item,
      color: getColorSet()[index],
    }));
  };

  const categoriesWithPercentage = calculatePercentages(getColoredCategories());

  const updateChartData = () => {
    const data = {
      datasets: [
        {
          // datasets accept an array of values. To represent "zero state", we pass an array with one value (100).
          // This will render the entire Doughnut as the design
          data: expensesChartData?.categoriesItems.length > 0 ? extractData(expensesChartData.categoriesItems) : [100],
          backgroundColor: getColorSet(),
          hoverBackgroundColor: getColorSet(),
          hoverBorderColor: getColorSet(),
        },
      ],
    };
    setChartData(data);
  };

  useEffect(() => {
    updateChartData();
    const options = {
      cutout: '80%',
      hoverBorderWidth: 3,
      borderWidth: 0,
      radius: 60,
      animation: false,
      onClick: (event: any, elements: any) => {
        if (elements.length) {
          const { index } = elements[0];

          setActiveCategory(categoriesWithPercentage[index]);
          setActiveIndex(index);
        }
      },
      hover: {
        onHover: (event: any) => {
          event.native.preventDefault();
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    };
    setChartOptions(options);

    // make the chart smaller to fit the widget
    if (chartRef.current) {
      const canvas = chartRef.current.getCanvas();

      canvas.parentNode.style.height = '128px';
      canvas.parentNode.style.width = '128px';
    }
    setTimeout(() => {
      if (categoriesWithPercentage?.[0]) {
        const elem = document.getElementById(`LabelBubble__${categoriesWithPercentage[0].categoryId}`);
        if (elem) elem.click();
      }
    }, 500);
  }, []);

  useEffect(() => {
    updateChartData();
  }, [expensesChartData]);

  useEffect(() => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getChart(); // Get chart instance

      /** **************
             TODO: PLEASE NOTE! eslint-disable-next-line is required here.
             Hack to attempt and remove the mouseout listener from the chart.
             It works un consistently and is not a reliable solution.
             It will be removed once a better solution is found.
             Meanwhile it's better then nothing
             ************* */
      // eslint-disable-next-line no-underscore-dangle
      if (chartInstance && chartInstance._listeners) {
        // eslint-disable-next-line no-underscore-dangle
        delete chartInstance._listeners.mouseout;
        // eslint-disable-next-line no-underscore-dangle
        delete chartInstance._listeners.mousemove;
        // eslint-disable-next-line no-underscore-dangle
        chartInstance._listeners.mouseout = () => {};
        // eslint-disable-next-line no-underscore-dangle
        chartInstance._listeners.mousemove = null;
      }

      if (!chartInstance) return;

      const meta = chartInstance.getDatasetMeta(0); // Get metadata of the dataset

      // Loop through all elements
      // Set the 'hidden' property to 'true' for all elements except the active one
      for (let i = 0; i < meta.data.length; i++) {
        meta.data[i].active = i === activeIndex;
      }

      // Update the chart to reflect the changes
      chartInstance.update();
    }
  }, [activeIndex, activeCategory]);

  return (
    <div
      className={`flex ${expensesChartData?.categoriesItems.length > 0 ? '' : 'justify-center'}  my-5`}
      data-testid='DoughnutChart__wrapper'
    >
      {expensesChartData?.categoriesItems?.length > 0 && (
        <div className='flex flex-col justify-center flex-1'>
          {categoriesWithPercentage.map((item: CategoriesItem, index: number) => (
            <LabelBubble
              key={item.categoryId}
              item={item}
              isActive={item.categoryId === activeCategory?.categoryId}
              onItemClick={() => {
                setActiveCategory(item);
                setActiveIndex(index);
              }}
            />
          ))}
        </div>
      )}
      <div className='relative'>
        {expensesChartData?.categoriesItems?.length > 0 && activeCategory && (
          <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-black z-10'>
            <p
              className='font-metricMedium text-[14px] text-center text-bw08'
              data-testid='DoughnutChart__activeCategory__total'
            >
              {convertValueToUSD(activeCategory?.total, 2)}
            </p>
            <p className='font-metricR text-[14px] text-center' data-testid='DoughnutChart__activeCategory__percentage'>
              {/* @ts-ignore */}
              {activeCategory?.percentage}
              %
            </p>
          </div>
        )}
        <Chart
          ref={chartRef}
          type='doughnut'
          data={chartData}
          options={chartOptions}
          className='w-full md:w-30rem top-1/2 -translate-y-1/2 '
        />
      </div>
    </div>
  );
};
